import { Component, Input, OnChanges, OnInit, SimpleChanges } from "@angular/core";
import { CartService } from "../../../cart.service";
import { Attachment, InventoryService } from "../../../../inventory/inventory";
import { isWeeklyDuration, MONTHLY_TO_WEEKLY_MULTIPLIER } from "../../../rental-duration-billing";

/**
 * In-place attachment picker for add-to-cart. Shown under transportation on the
 * dates step. No Install UI — install is set silently on select/deselect for
 * cart compatibility. Pills show Free / $X per billing period (weekly rentals
 * show the weekly rate, everything else the monthly rate) and highlight when
 * selected.
 */
@Component({
  selector: "common-add-to-cart-attachments",
  templateUrl: "./add-to-cart-attachments.component.html",
  styleUrls: ["./add-to-cart-attachments.component.scss"],
})
export class AddToCartAttachmentsComponent implements OnInit, OnChanges {
  @Input() machineId: string;
  /** Fallback account/partition for cart API when the attachments index row is missing. */
  @Input() machineAccountId: string;
  @Input() attachments: Attachment[];
  @Input() attachmentsFromIndex: Attachment[];
  /** Selected rental duration in days; decides whether pills show weekly or monthly rates. */
  @Input() duration: number = 0;

  standardAttachments: Attachment[] = [];
  nonStandardAttachments: Attachment[] = [];

  /** Whether the full attachment picker is expanded. */
  showPicker = false;

  constructor(private inventoryService: InventoryService, private cartService: CartService) {}

  ngOnInit(): void {
    this.enrichAttachments();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Re-enrich when Azure Search results arrive after the picker already mounted.
    if (changes.attachmentsFromIndex && !changes.attachmentsFromIndex.firstChange) {
      this.enrichAttachments();
    }
  }

  private enrichAttachments(): void {
    const attachmentIdsInCart = this.cartService.getOtherAttachmentIds(this.machineId);
    for (const attachment of this.attachments || []) {
      // Cart estimate/create requires these even if the attachments index row is missing.
      attachment.childItemId = attachment.inventoryItemId;
      if (attachment.price == null) {
        attachment.price = attachment.cost;
      }

      const attachmentFromIndex = this.attachmentsFromIndex?.find(d => d.id === attachment.inventoryItemId);
      if (attachmentFromIndex) {
        if (attachmentFromIndex.specifications?.length) {
          attachment.specifications = attachmentFromIndex.specifications;
          attachment.description = this.inventoryService.getFormattedSpecifications(attachmentFromIndex.specifications);
        }

        if (attachmentFromIndex.displayName) {
          attachment.displayName = attachmentFromIndex.displayName;
        }

        attachment.compatibleWith = attachmentFromIndex.compatibleWith;
        attachment.accountId = attachmentFromIndex.accountId;
      }

      attachment.accountId = attachment.accountId || this.machineAccountId;
      attachment.alreadyInCart = attachmentIdsInCart.includes(attachment.inventoryItemId);
    }

    this.standardAttachments = (this.attachments || []).filter(d => d.standard);
    this.nonStandardAttachments = (this.attachments || []).filter(d => !d.standard);

    // Open the picker when there are attachments so Standard/Specialty are visible immediately.
    if ((this.attachments || []).length) {
      this.showPicker = true;
    }
  }

  /**
   * Format price for pills: Free when $0, otherwise the rate for the selected
   * billing term — $X/wk for weekly rentals (< 21 days), $X/mo otherwise.
   */
  priceLabel(attachment: Attachment): string {
    if (attachment.alreadyInCart) {
      return "In cart";
    }
    if (attachment.price === 0) {
      return "Free";
    }

    const monthly = Number(attachment.price) || 0;
    if (isWeeklyDuration(this.duration)) {
      // Prefer the owner-set weekly rate (hydrated from the association API);
      // otherwise derive it from the monthly rate the same way billing does
      // when no weekly is specified (BillingConstants.DefaultWeeklyMultiplier).
      const weekly = Number(attachment.weeklyPrice) || Math.round(monthly * MONTHLY_TO_WEEKLY_MULTIPLIER);
      return `$${weekly.toLocaleString("en-US", { maximumFractionDigits: 0 })}/wk`;
    }

    return `$${monthly.toLocaleString("en-US", { maximumFractionDigits: 0 })}/mo`;
  }

  selectAttachment(attachment: Attachment): void {
    if (attachment.alreadyInCart) {
      return;
    }

    attachment.selected = !attachment.selected;

    if (!attachment.selected) {
      attachment.install = false;
      if (attachment.price === 0) {
        const selectedAttachment = this.standardAttachments.find(d => d.selected);
        if (selectedAttachment) {
          selectedAttachment.price = 0;
        }
      }
      attachment.price = attachment.cost;
      return;
    }

    attachment.install = true;
    attachment.childItemId = attachment.childItemId || attachment.inventoryItemId;
    attachment.accountId = attachment.accountId || this.machineAccountId;

    if (attachment.standard) {
      const otherStandardSelected = this.standardAttachments.some(
        d => d.selected && d.inventoryItemId !== attachment.inventoryItemId,
      );
      attachment.price = otherStandardSelected ? attachment.cost : 0;
    } else {
      attachment.price = attachment.cost;
    }
  }
}
